import { useState } from 'react';
import { Alert, Badge, Box, Button, Heading, SelectInputNative, Spinner, Table, TextInput } from '@hyphen/hyphen-components';

import { api } from '../lib/api.js';
import { useResource } from '../lib/session.js';

// What each role means, shown where a role is chosen: three names in a dropdown
// say nothing about which one an outside firm should get.
const ROLES = [
  { value: 'viewer', label: 'viewer', help: 'Reads the controls and the evidence.' },
  { value: 'collaborator', label: 'collaborator', help: 'Reads them, and comments on a control.' },
  { value: 'editor', label: 'editor', help: 'Edits everything, and sees the gate and this page.' },
];

const ROLE_COLOR = { editor: 'info', collaborator: 'warning', viewer: 'grey' };

/** Who may sign in, and what they may do. */
export function UsersAdmin({ session }) {
  const { data: users, error, setData, setError } = useResource('/users');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('viewer');
  const [busy, setBusy] = useState(null);

  async function act(key, fn) {
    setBusy(key);
    setError(null);
    try {
      setData(await fn());
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(null);
    }
  }

  function add(event) {
    event.preventDefault();
    const address = email.trim();
    if (!address) return;
    // Cleared only once the request succeeds. Clearing first means a failed
    // add — a network blip, a rejected role — leaves the person retyping an
    // address they already typed.
    act(address, async () => {
      const updated = await api.put(`/users/${encodeURIComponent(address)}`, { role });
      setEmail('');
      return updated;
    });
  }

  if (error && !users) return <Alert variant="danger">{error}</Alert>;
  if (!users) return <Spinner />;

  const columns = [
    { heading: 'Email', dataKey: 'email' },
    {
      heading: 'Role',
      dataKey: 'role',
      width: 280,
      render: (_cell, row) =>
        // Editable in place. Changing what someone may do was possible only by
        // removing them and adding them back, which is a different act — it
        // drops them out of the allowlist in between.
        session.canEdit ? (
          <Role
            key={`${row.email}:${row.role}`}
            user={row}
            saving={busy === row.email}
            onSave={(role) =>
              act(row.email, () =>
                api.put(`/users/${encodeURIComponent(row.email)}`, { role }))
            }
          />
        ) : (
          <Badge variant="soft" color={ROLE_COLOR[row.role] ?? 'grey'}>{row.role}</Badge>
        ),
    },
    {
      heading: '',
      dataKey: 'actions',
      width: 100,
      render: (_cell, row) =>
        // Removing your own access locks you out of the tool you manage access
        // with, so the API refuses it too — this only hides a button that would
        // always fail.
        session.canEdit && row.email !== session.email ? (
          <Button
            variant="danger"
            size="sm"
            isLoading={busy === row.email}
            onClick={() => act(row.email, () => api.delete(`/users/${encodeURIComponent(row.email)}`))}
          >
            Remove
          </Button>
        ) : null,
    },
  ];

  return (
    <Box gap="3xl" maxWidth="48rem">
      <Heading as="h1" size="lg">Access</Heading>

      {error && <Alert variant="warning">{error}</Alert>}

      <Table rowKey="email" columns={columns} rows={users} />

      <Box gap="xs">
        {ROLES.map(({ value, label, help }) => (
          <Box key={value} direction="row" gap="sm" fontSize="sm">
            <Box as="span" fontWeight="medium" width="7rem">{label}</Box>
            <Box as="span" color="secondary">{help}</Box>
          </Box>
        ))}
      </Box>

      {session.canEdit && (
        <Box as="form" direction="row" alignItems="flex-end" gap="lg" onSubmit={add}>
          <Box flex="auto">
            <TextInput
              id="new-user-email"
              label="Email address"
              type="email"
              placeholder="name@hyphen.ai"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </Box>
          <Box width="10rem">
            <SelectInputNative
              id="new-user-role"
              label="Role"
              value={role}
              options={ROLES.map(({ value, label }) => ({ value, label }))}
              onChange={(event) => setRole(event.target.value)}
            />
          </Box>
          <Button type="submit" variant="primary" isDisabled={Boolean(busy)}>Add</Button>
        </Box>
      )}
    </Box>
  );
}

/**
 * One person's role, changed deliberately.
 *
 * A dropdown that saved on selection changed what someone may do on the way
 * past it, with a mis-click indistinguishable from a decision — and the
 * control's fields ask for Save, so this asking for nothing was its own
 * surprise. Keyed on the stored role above, so a saved change rebuilds this
 * with the new value as its starting point.
 */
function Role({ user, saving, onSave }) {
  const [draft, setDraft] = useState(user.role);
  const changed = draft !== user.role;

  return (
    <Box direction="row" alignItems="center" gap="sm">
      <Box width="9rem">
        <SelectInputNative
          id={`role-${user.email}`}
          label={`Role for ${user.email}`}
          hideLabel
          value={draft}
          options={ROLES.map(({ value, label }) => ({ value, label }))}
          isDisabled={saving}
          onChange={(event) => setDraft(event.target.value)}
        />
      </Box>
      {changed && (
        <>
          <Button size="sm" isLoading={saving} onClick={() => onSave(draft)}>Save</Button>
          <Button size="sm" variant="secondary" isDisabled={saving} onClick={() => setDraft(user.role)}>
            Cancel
          </Button>
        </>
      )}
    </Box>
  );
}
